import React from 'react';
import { formatCurrency, getPropertyTypeName, getAmenitiesByIds } from '../../../constants/propertyConstants';
import { MapPin, Home, DollarSign, Calendar, Check, Image } from 'lucide-react';

const Step7Review = ({ formData, images }) => {
    const selectedAmenities = getAmenitiesByIds(formData.amenityIds || []);

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-semibold text-soil-900 mb-4">Review Your Listing</h3>
                <p className="text-sm text-soil-600 mb-6">
                    Please review all the information before submitting
                </p>
            </div>

            {/* Basic Information */}
            <div className="bg-white border border-soil-100 rounded-lg p-6">
                <div className="flex items-center mb-4">
                    <Home className="h-5 w-5 text-highland-500 mr-2" />
                    <h4 className="text-md font-semibold text-soil-900">Basic Information</h4>
                </div>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Property Name</dt>
                        <dd className="mt-1 text-sm text-soil-900">{formData.propertyName || 'Not provided'}</dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Property Type</dt>
                        <dd className="mt-1 text-sm text-soil-900">{getPropertyTypeName(formData.propertyTypeId)}</dd>
                    </div>
                    <div className="md:col-span-2">
                        <dt className="text-sm font-medium text-soil-400">Description</dt>
                        <dd className="mt-1 text-sm text-soil-900">{formData.propertyDescription || 'Not provided'}</dd>
                    </div>
                </dl>
            </div>

            {/* Location */}
            <div className="bg-white border border-soil-100 rounded-lg p-6">
                <div className="flex items-center mb-4">
                    <MapPin className="h-5 w-5 text-highland-500 mr-2" />
                    <h4 className="text-md font-semibold text-soil-900">Location</h4>
                </div>
                <dl className="space-y-2">
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Address</dt>
                        <dd className="mt-1 text-sm text-soil-900">
                            {formData.addressLine1}
                            {formData.addressLine2 && `, ${formData.addressLine2}`}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-soil-400">City</dt>
                        <dd className="mt-1 text-sm text-soil-900">
                            {formData.city}{formData.stateProvince && `, ${formData.stateProvince}`}
                            {formData.country && `, ${formData.country}`}
                        </dd>
                    </div>
                    {formData.neighborhood && (
                        <div>
                            <dt className="text-sm font-medium text-soil-400">Neighborhood</dt>
                            <dd className="mt-1 text-sm text-soil-900">{formData.neighborhood}</dd>
                        </div>
                    )}
                </dl>
            </div>

            {/* Property Details */}
            <div className="bg-white border border-soil-100 rounded-lg p-6">
                <h4 className="text-md font-semibold text-soil-900 mb-4">Property Details</h4>
                <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Bedrooms</dt>
                        <dd className="mt-1 text-sm text-soil-900">{formData.bedrooms || 0}</dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Bathrooms</dt>
                        <dd className="mt-1 text-sm text-soil-900">{formData.bathrooms || 0}</dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Square Feet</dt>
                        <dd className="mt-1 text-sm text-soil-900">{formData.squareFeet || 'N/A'}</dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Year Built</dt>
                        <dd className="mt-1 text-sm text-soil-900">{formData.yearBuilt || 'N/A'}</dd>
                    </div>
                </dl>
            </div>

            {/* Pricing & Terms */}
            <div className="bg-white border border-soil-100 rounded-lg p-6">
                <div className="flex items-center mb-4">
                    <DollarSign className="h-5 w-5 text-highland-500 mr-2" />
                    <h4 className="text-md font-semibold text-soil-900">Pricing & Terms</h4>
                </div>
                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Monthly Rent</dt>
                        <dd className="mt-1 text-lg font-semibold text-soil-900">
                            {formatCurrency(formData.pricePerMonth, formData.currency)}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Security Deposit</dt>
                        <dd className="mt-1 text-sm text-soil-900">
                            {formData.securityDeposit ? formatCurrency(formData.securityDeposit, formData.currency) : 'Not specified'}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Lease Type</dt>
                        <dd className="mt-1 text-sm text-soil-900 capitalize">{formData.leaseType || 'N/A'}</dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Minimum Term</dt>
                        <dd className="mt-1 text-sm text-soil-900">{formData.minimumLeaseTerm} months</dd>
                    </div>
                    <div>
                        <dt className="text-sm font-medium text-soil-400">Furnishing</dt>
                        <dd className="mt-1 text-sm text-soil-900 capitalize">{formData.furnishingStatus || 'N/A'}</dd>
                    </div>
                </dl>
            </div>

            {/* Features & Amenities */}
            <div className="bg-white border border-soil-100 rounded-lg p-6">
                <div className="flex items-center mb-4">
                    <Check className="h-5 w-5 text-highland-500 mr-2" />
                    <h4 className="text-md font-semibold text-soil-900">Features & Amenities</h4>
                </div>

                <div className="mb-4">
                    <h5 className="text-sm font-medium text-soil-600 mb-2">Property Features</h5>
                    <div className="flex flex-wrap gap-2">
                        {formData.hasParking && (
                            <span className="px-3 py-1 bg-highland-50 text-highland-600 text-xs font-medium rounded-full">
                                Parking ({formData.parkingSpaces} spaces)
                            </span>
                        )}
                        {formData.hasElevator && (
                            <span className="px-3 py-1 bg-highland-50 text-highland-600 text-xs font-medium rounded-full">
                                Elevator
                            </span>
                        )}
                        {formData.hasSecurity && (
                            <span className="px-3 py-1 bg-highland-50 text-highland-600 text-xs font-medium rounded-full">
                                24/7 Security
                            </span>
                        )}
                        {formData.hasWifi && (
                            <span className="px-3 py-1 bg-highland-50 text-highland-600 text-xs font-medium rounded-full">
                                WiFi
                            </span>
                        )}
                        {formData.hasGenerator && (
                            <span className="px-3 py-1 bg-highland-50 text-highland-600 text-xs font-medium rounded-full">
                                Generator
                            </span>
                        )}
                        {formData.hasWaterBackup && (
                            <span className="px-3 py-1 bg-highland-50 text-highland-600 text-xs font-medium rounded-full">
                                Water Backup
                            </span>
                        )}
                        {formData.petFriendly && (
                            <span className="px-3 py-1 bg-highland-50 text-highland-600 text-xs font-medium rounded-full">
                                Pet Friendly
                            </span>
                        )}
                        {formData.smokingAllowed && (
                            <span className="px-3 py-1 bg-soil-100 text-soil-900 text-xs font-medium rounded-full">
                                Smoking Allowed
                            </span>
                        )}
                    </div>
                </div>

                {selectedAmenities.length > 0 && (
                    <div>
                        <h5 className="text-sm font-medium text-soil-600 mb-2">Amenities</h5>
                        <div className="flex flex-wrap gap-2">
                            {selectedAmenities.map((amenity) => (
                                <span
                                    key={amenity.id}
                                    className="px-3 py-1 bg-soil-100 text-soil-900 text-xs font-medium rounded-full"
                                >
                                    {amenity.name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Images */}
            <div className="bg-white border border-soil-100 rounded-lg p-6">
                <div className="flex items-center mb-4">
                    <Image className="h-5 w-5 text-highland-500 mr-2" />
                    <h4 className="text-md font-semibold text-soil-900">Images</h4>
                </div>
                <p className="text-sm text-soil-600">
                    {images.length > 0
                        ? `${images.length} image(s) uploaded`
                        : 'No images uploaded'}
                </p>
            </div>

            <div className="bg-highland-50 border border-highland-100 rounded-lg p-4">
                <p className="text-sm text-highland-600">
                    <strong>Ready to submit!</strong> Review all information above and click "Save as Draft" or "Publish Now" below.
                </p>
            </div>
        </div>
    );
};

export default Step7Review;
